/**
 * SegmentAttachment - Loads and manages per-Gaussian segment data for hover highlighting.
 *
 * Pattern follows SplatEditAttachment:
 * 1. Fetches .segments.bin (Uint16Array, one segment ID per Gaussian)
 * 2. Creates a GPU texture (R16U) mapping splat index → segment ID
 * 3. Provides uniform for highlighted segment ID
 * 4. Injects shader code via gsplatModifyVS to tint hovered segment
 */
import * as pc from 'playcanvas';

export interface SegmentMeta {
    id: number;
    name: string;
    gaussian_count: number;
    centroid_3d: [number, number, number];
    avg_color: [number, number, number];
}

export interface SegmentMetadata {
    version: number;
    total_gaussians: number;
    segments: SegmentMeta[];
}

export class SegmentAttachment {
    readonly entity: pc.Entity;
    readonly device: pc.GraphicsDevice;

    /** Per-splat segment IDs (CPU-side) */
    segmentIds: Uint16Array;
    /** GPU texture mapping splat index → segment ID */
    segmentTexture: pc.Texture;
    /** Metadata about each segment */
    metadata: SegmentMetadata | null = null;
    /** Currently highlighted segment ID (0 = none) */
    highlightedSegment: number = 0;

    private _material: pc.Material | null = null;
    private _texWidth: number;
    private _texHeight: number;
    private _numSplats: number;

    constructor(entity: pc.Entity, segmentData: Uint16Array, numSplats: number) {
        this.entity = entity;
        const root = entity.root as any;
        this.device = root?.app?.graphicsDevice ?? (pc as any).app?.graphicsDevice;

        if (!this.device) {
            throw new Error('SegmentAttachment: cannot find graphics device');
        }

        this._numSplats = numSplats;

        // Infer texture dimensions from gsplat component
        const gsplatComponent = entity.gsplat;
        if (!gsplatComponent) {
            throw new Error('SegmentAttachment: entity has no gsplat component');
        }

        const instance = (gsplatComponent as any).instance;
        const placement = (gsplatComponent as any)._placement;
        const resource = (gsplatComponent as any).asset?.resource
            ?? (instance as any)?.resource
            ?? (placement as any)?.resource;
        const texDims = (resource as any)?.textureDimensions
            ?? (resource as any)?.streams?.textureDimensions;

        if (texDims) {
            this._texWidth = texDims.x;
            this._texHeight = texDims.y;
        } else {
            // Fallback: square texture
            const side = Math.ceil(Math.sqrt(numSplats));
            this._texWidth = side;
            this._texHeight = side;
        }

        // Create CPU-side array (pad to texture size)
        this.segmentIds = new Uint16Array(this._texWidth * this._texHeight);
        // Copy segment data (may be shorter than texture if not all pixels are splats)
        const copyLen = Math.min(segmentData.length, this.segmentIds.length);
        this.segmentIds.set(segmentData.subarray(0, copyLen));

        // Create GPU texture
        this.segmentTexture = new pc.Texture(this.device, {
            name: 'segmentIds',
            width: this._texWidth,
            height: this._texHeight,
            format: pc.PIXELFORMAT_R16U,
            mipmaps: false,
            minFilter: pc.FILTER_NEAREST,
            magFilter: pc.FILTER_NEAREST,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });

        this._uploadSegments();
    }

    /**
     * Set highlight uniform on the gsplat material.
     * Must be called each frame or when highlighted segment changes.
     */
    updateMaterialParams(): void {
        if (!this._material) {
            // Try to find the material from the gsplat component
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;
            const instance = (gsplat as any).instance ?? (gsplat as any)._placement;
            if (!instance) return;
            this._material = (instance as any).material ?? (instance as any).meshInstance?.material;
        }
        if (!this._material) return;

        this._material.setParameter('segmentTexture', this.segmentTexture);
        this._material.setParameter('highlightedSegment', this.highlightedSegment);
        this._material.update();
    }

    /**
     * Get segment ID at a given splat index.
     */
    getSegmentAtSplat(splatIndex: number): number {
        if (splatIndex < 0 || splatIndex >= this._numSplats) return 0;
        return this.segmentIds[splatIndex];
    }

    /**
     * Set the highlighted segment (0 = none).
     */
    setHighlight(segmentId: number): void {
        if (this.highlightedSegment === segmentId) return;
        this.highlightedSegment = segmentId;
        this.updateMaterialParams();
    }

    /**
     * Clear the highlight.
     */
    clearHighlight(): void {
        this.setHighlight(0);
    }

    destroy(): void {
        this.segmentTexture.destroy();
        this._material = null;
    }

    private _uploadSegments(): void {
        const raw = this.segmentTexture.lock();
        const lock = raw instanceof Uint16Array
            ? raw
            : new Uint16Array(raw instanceof ArrayBuffer ? raw : (raw as Uint8Array).buffer);
        lock.set(this.segmentIds.subarray(0, lock.length));
        this.segmentTexture.unlock();
    }
}

/**
 * Fetch and create a SegmentAttachment for a gsplat entity.
 */
export async function loadSegmentAttachment(
    entity: pc.Entity,
    segmentDataUrl: string,
    segmentMetaUrl?: string,
): Promise<SegmentAttachment> {
    // Fetch binary segment data
    const binResponse = await fetch(segmentDataUrl);
    if (!binResponse.ok) {
        throw new Error(`Failed to fetch segment data: ${binResponse.status}`);
    }
    const binBuffer = await binResponse.arrayBuffer();
    const segmentData = new Uint16Array(binBuffer);

    const numSplats = segmentData.length;
    const attachment = new SegmentAttachment(entity, segmentData, numSplats);

    // Optionally fetch metadata
    if (segmentMetaUrl) {
        try {
            const metaResponse = await fetch(segmentMetaUrl);
            if (metaResponse.ok) {
                attachment.metadata = await metaResponse.json();
            }
        } catch (e) {
            console.warn('[SegmentAttachment] Failed to load segment metadata:', e);
        }
    }

    return attachment;
}
